import { useMemo, useRef, useState, type ComponentType } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Button, DeleteModal, Table } from "@telluscompany/design-system";
import { Download, PlusCircle } from "lucide-react";

import { useLocalization } from "@i18n";
import { Gs3TablePaginator, LoadingBlurDiv } from "@shared/components";
import { useUserContext } from "@shared/contexts";
import {
  DEFAULT_PAGE_SIZE,
  EMPTY_PAGINATION_META,
} from "@shared/constants/pagination.constants";
import { useUpdateTableUserPreferences } from "@shared/hooks";

import {
  useDeleteInspectionMutation,
  useInspectionListQuery,
  type InspectionCapabilities,
  type InspectionCreationOrigin,
  type InspectionListItem,
  type InspectionListParams,
  type InspectionStatus,
} from "../../api/inspection";
import { formatInspectionDate } from "../../utils/inspectionDate";
import {
  EMPTY_INSPECTION_FILTERS,
  hasInspectionFilters,
  toInspectionListParams,
  type InspectionFilterValues,
} from "../../utils/inspectionFilters";
import {
  inspectionOriginLabelKey,
  inspectionStatusLabelKey,
  isInspectionDeletable,
  isInspectionEditableStatus,
} from "../../utils/inspectionLabels";
import InspectionCreateModal from "./InspectionCreateModal";
import InspectionFormModal from "./InspectionFormModal";
import InspectionReportPreviewModal from "./InspectionReportPreviewModal";
import InspectionsActionMenu from "./InspectionsActionMenu";
import InspectionsFilterPanel from "./InspectionsFilterPanel";

export interface InspectionsTableProps {
  structureId: string;
  capabilities: InspectionCapabilities;
}

type FichaModalState = { mode: "edit" | "view"; inspectionId: string } | null;

const DEFAULT_SORT_BY = "inspectionDate";
const DEFAULT_SORT_ORDER = "desc";
const TABLE_ID = "inspection-inspections-table";

const statusBadgeClass = (status: InspectionStatus) =>
  ({
    open: "st-open",
    inProgress: "st-inProgress",
    finalized: "st-finalized",
    notFinalized: "st-notFinalized",
  })[status];

const originBadgeClass = (origin: InspectionCreationOrigin) =>
  origin === "scheduled" ? "or-scheduled" : "or-manual";

/**
 * Listagem de inspeções da estrutura (inspection.md §6.0 / §6.2).
 * Tabela, filtro, ações e paginação no padrão Admin (`Table` + `TableFilter`
 * + `ActionMenu` + `Gs3TablePaginator`).
 */
const InspectionsTable = ({
  structureId,
  capabilities,
}: InspectionsTableProps) => {
  const { translate, language } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(
    me?.userPreferences,
  );
  const [searchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<InspectionFilterValues>(
    EMPTY_INSPECTION_FILTERS,
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [fichaModal, setFichaModal] = useState<FichaModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<InspectionListItem | null>(
    null,
  );
  const [previewTarget, setPreviewTarget] = useState<InspectionListItem | null>(
    null,
  );
  const sortBy = searchParams.get("sortBy") ?? DEFAULT_SORT_BY;
  const sortOrder = searchParams.get("sortOrder") ?? DEFAULT_SORT_ORDER;

  const params: InspectionListParams = useMemo(
    () => ({
      page,
      limit,
      sortBy,
      sortOrder,
      ...toInspectionListParams(filters),
    }),
    [filters, limit, page, sortBy, sortOrder],
  );

  const { data, isFetching, isPending, error, refetch } = useInspectionListQuery(
    structureId,
    params,
    { placeholderData: keepPreviousData },
  );
  const lastSuccessfulList = useRef(data);
  if (data) {
    lastSuccessfulList.current = data;
  }
  const list = data ?? lastSuccessfulList.current;

  const deleteMutation = useDeleteInspectionMutation(structureId);

  const rows = useMemo(() => list?.data ?? [], [list]);
  const isFiltered = hasInspectionFilters(filters);
  const showInitialLoading = isPending && !list;

  const paginationMeta = list?.meta ?? {
    ...EMPTY_PAGINATION_META,
    currentPage: page,
    itemsPerPage: limit,
  };

  const applyFilters = (nextFilters: InspectionFilterValues) => {
    setPage(1);
    setFilters(nextFilters);
  };

  /** Abre a ficha: edição quando há `create` + status editável; senão leitura.
   * Programada `Aberta` só promove no primeiro save (Y=6/Y=11), não ao abrir. */
  const openFicha = (row: InspectionListItem, preferEdit: boolean) => {
    setFichaModal({
      mode: preferEdit ? "edit" : "view",
      inspectionId: row.id,
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;

    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        void refetch();
      },
    });
  };

  const resolveRow = (rowData: InspectionListItem) =>
    rows.find((item) => item.id === rowData.id) ?? rowData;

  const createActionMenu = (payload: { rowData: InspectionListItem }) => {
    const row = resolveRow(payload.rowData);
    const canEdit =
      capabilities.create && isInspectionEditableStatus(row.inspectionStatus);
    const canDelete =
      capabilities.delete &&
      isInspectionDeletable(row.inspectionStatus, row.creationOrigin);

    return (
      <InspectionsActionMenu
        row={row}
        canDelete={canDelete}
        onOpen={() => openFicha(row, canEdit)}
        onDelete={() => setDeleteTarget(row)}
      />
    );
  };

  const tableData = useMemo(
    () =>
      rows.map((row) => {
        const hasReport = row.inspectionStatus === "finalized" && row.hasPdf;

        return {
          ...row,
          responsibleName:
            row.responsibleName ?? translate("inspection.table.fallback"),
          inspectionDate: formatInspectionDate(row.inspectionDate, language),
          conservationStateScore:
            row.conservationStateScore === null
              ? translate("inspection.table.fallback")
              : row.conservationStateScore,
          inspectionStatus: (
            <span className={`insp-badge ${statusBadgeClass(row.inspectionStatus)}`}>
              {translate(inspectionStatusLabelKey(row.inspectionStatus))}
            </span>
          ),
          creationOrigin: (
            <span className={`insp-badge ${originBadgeClass(row.creationOrigin)}`}>
              {translate(inspectionOriginLabelKey(row.creationOrigin))}
            </span>
          ),
          pdf: capabilities.view ? (
            <span
              className="relative inline-flex"
              title={
                hasReport
                  ? translate("inspection.table.pdfPreviewTitle")
                  : translate("inspection.table.pdfAvailableAfterFinalize")
              }
            >
              <Button
                type="button"
                size="small"
                variant={hasReport ? "primary" : "secondary"}
                disabled={!hasReport}
                className="h-7 min-h-0 px-2 py-0"
                aria-label={translate("inspection.table.columns.pdf")}
                onClick={() => setPreviewTarget(row)}
                iconLeft={<Download aria-hidden size={14} />}
              >
                {translate("inspection.actions.downloadReport")}
              </Button>
              {!hasReport ? (
                <span
                  data-testid="inspection-download-unavailable"
                  className="insp-download-unavailable cursor-not-allowed"
                  aria-hidden
                />
              ) : null}
            </span>
          ) : null,
        };
      }),
    [capabilities, language, rows, translate],
  );

  const emptyMessage = isFiltered
    ? translate("inspection.table.emptyFiltered")
    : translate("inspection.table.empty");

  return (
    <div className="gs3-ds-table p-4">
      {error?.parsedMessage ? (
        <p role="alert" className="mb-2 text-sm text-red-600">
          {error.parsedMessage}
        </p>
      ) : null}

      {showInitialLoading ? (
        <LoadingBlurDiv isLoading>
          <p role="status" className="text-sm text-[var(--insp-muted)]">
            {translate("inspection.loading")}
          </p>
        </LoadingBlurDiv>
      ) : (
      <LoadingBlurDiv isLoading={isFetching}>
      <Table<InspectionListItem>
        userId={me?.id ?? ""}
        tableId={TABLE_ID}
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={
          capabilities.view
            ? (createActionMenu as ComponentType)
            : undefined
        }
        TableButtons={
          capabilities.create ? (
            <Button
              size="small"
              variant="primary"
              onClick={() => {
                setIsCreateOpen(true);
              }}
              iconLeft={<PlusCircle />}
            >
              {translate("inspection.actions.new")}
            </Button>
          ) : undefined
        }
        TableFilter={() => (
          <InspectionsFilterPanel
            initialFilters={filters}
            onApply={applyFilters}
            onClear={() => applyFilters(EMPTY_INSPECTION_FILTERS)}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginationMeta}
            useSearchParamsHook={useSearchParams}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate("inspection.table.filterButtonText")}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "inspectionCode",
            label: translate("inspection.table.columns.code"),
            minWidth: "140px",
            sortType: "number",
          },
          {
            key: "responsibleName",
            label: translate("inspection.table.columns.responsible"),
            minWidth: "260px",
            sortType: "text",
          },
          {
            key: "inspectionDate",
            label: translate("inspection.table.columns.date"),
            minWidth: "120px",
            sortType: "date",
          },
          {
            key: "conservationStateScore",
            label: translate("inspection.table.columns.conservationState"),
            minWidth: "200px",
            sortType: "number",
          },
          {
            key: "anomalyCount",
            label: translate("inspection.table.columns.anomalies"),
            minWidth: "150px",
            sortType: "number",
          },
          {
            key: "inspectionStatus",
            label: translate("inspection.table.columns.status"),
            minWidth: "140px",
            sortType: "text",
          },
          {
            key: "creationOrigin",
            label: translate("inspection.table.columns.origin"),
            minWidth: "140px",
            sortType: "text",
          },
          {
            key: "pdf",
            label: translate("inspection.table.columns.pdf"),
            minWidth: "160px",
          },
        ]}
        tableData={
          tableData as { [key: string]: unknown; color?: string }[]
        }
      />

      {!isFetching && !error && rows.length === 0 ? (
        <p className="insp-empty-box">{emptyMessage}</p>
      ) : null}
      </LoadingBlurDiv>
      )}

      <DeleteModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isDeleting={deleteMutation.isPending}
        itemDescription={
          deleteTarget
            ? translate("inspection.delete.item", {
                code: deleteTarget.inspectionCode,
                date: formatInspectionDate(deleteTarget.inspectionDate, language),
              })
            : ""
        }
        title={translate("inspection.delete.title")}
        message={translate("inspection.delete.caption")}
        alertMessage={translate("inspection.delete.description")}
        confirmButtonText={translate("inspection.delete.confirm")}
        cancelButtonText={translate("inspection.delete.cancel")}
        deletingButtonText={translate("inspection.delete.confirm")}
      />

      <InspectionReportPreviewModal
        isOpen={Boolean(previewTarget)}
        onClose={() => setPreviewTarget(null)}
        structureId={structureId}
        inspection={previewTarget}
      />

      <InspectionCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        structureId={structureId}
        onCreated={(created) => {
          setIsCreateOpen(false);
          setFichaModal({ mode: "edit", inspectionId: created.id });
        }}
      />

      {fichaModal ? (
        <InspectionFormModal
          isOpen
          onClose={() => setFichaModal(null)}
          structureId={structureId}
          capabilities={capabilities}
          mode={fichaModal.mode}
          inspectionId={fichaModal.inspectionId}
        />
      ) : null}
    </div>
  );
};

export default InspectionsTable;
